import React, {useState, useMemo} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

const {FiHome, FiTrendingUp, FiDollarSign, FiPieChart, FiX, FiInfo, FiCheckCircle} = FiIcons;

function RentVsBuyCalculator({onClose}) {
  const [formData, setFormData] = useState({
    propertyValue: 10000000,
    downPaymentPercent: 20,
    registrationCost: 600000,
    interiorsCost: 1500000,
    interestRate: 8.5,
    loanTenure: 20,
    monthlyRent: 30000,
    propertyAppreciation: 6,
    newFlatAppreciation: 10,
    mfReturn: 12,
    customSip: 0 // 0 means it will auto-calculate EMI - Rent
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: parseFloat(value) || 0}));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const calculations = useMemo(() => {
    const dp = formData.propertyValue * (formData.downPaymentPercent / 100);
    const loan = Math.max(0, formData.propertyValue - dp);
    const r = formData.interestRate / 100 / 12;
    const n = formData.loanTenure * 12;

    let emi = 0;
    if (r > 0) {
      emi = loan * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    } else if (n > 0) {
      emi = loan / n;
    }

    const defaultSip = Math.max(0, emi - formData.monthlyRent);
    const activeSip = formData.customSip > 0 ? formData.customSip : defaultSip;
    const lumpsum = dp + formData.registrationCost + formData.interiorsCost;

    // Future Property Value (Buy Scenario)
    const fvProperty = formData.propertyValue * Math.pow(1 + formData.propertyAppreciation / 100, formData.loanTenure);

    // Future New Flat Value (Rent Scenario context)
    const fvNewFlat = formData.propertyValue * Math.pow(1 + formData.newFlatAppreciation / 100, formData.loanTenure);

    // Future MF Value (Rent Scenario) - BEFORE TAX
    const mfMonthlyRate = formData.mfReturn / 100 / 12;
    const fvLumpsum = lumpsum * Math.pow(1 + formData.mfReturn / 100, formData.loanTenure);

    let fvSip = 0;
    if (mfMonthlyRate > 0) {
      fvSip = activeSip * ((Math.pow(1 + mfMonthlyRate, n) - 1) / mfMonthlyRate) * (1 + mfMonthlyRate);
    } else {
      fvSip = activeSip * n;
    }

    const totalMfValueBeforeTax = fvLumpsum + fvSip;

    // ═══════════════════════════════════════════════════════════════════════════
    // LTCG TAX CALCULATION FOR MUTUAL FUND ROUTE (Scenario B)
    // ═══════════════════════════════════════════════════════════════════════════
    
    // Total investment in MF
    const totalMFInvestment = lumpsum + (activeSip * 12 * formData.loanTenure);
    
    // Profit from MF
    const mfProfit = totalMfValueBeforeTax - totalMFInvestment;
    
    // Taxable gains (profit minus ₹1.25L exemption)
    const mfTaxableGains = Math.max(0, mfProfit - 125000);
    
    // LTCG Tax @ 12.5%
    const mfLTCGTax = mfTaxableGains * 0.125;
    
    // Total MF Value AFTER TAX
    const totalMfValueAfterTax = totalMfValueBeforeTax - mfLTCGTax;

    // Net wealth after buying new flat (using after-tax MF value)
    const savingsAfterNewFlat = totalMfValueAfterTax - fvNewFlat;

    // Wealth Comparison (After-tax MF vs Property)
    const wealthDifference = totalMfValueAfterTax - fvProperty;

    // Yearly breakdown for chart
    const yearlyData = [];
    for (let i = 1; i <= formData.loanTenure; i++) {
      const propVal = formData.propertyValue * Math.pow(1 + formData.propertyAppreciation / 100, i);
      const newFlatVal = formData.propertyValue * Math.pow(1 + formData.newFlatAppreciation / 100, i);
      
      const mF_Lump = lumpsum * Math.pow(1 + formData.mfReturn / 100, i);
      const months = i * 12;
      let mF_Sip = 0;
      if (mfMonthlyRate > 0) {
        mF_Sip = activeSip * ((Math.pow(1 + mfMonthlyRate, months) - 1) / mfMonthlyRate) * (1 + mfMonthlyRate);
      } else {
        mF_Sip = activeSip * months;
      }

      const mfValueBeforeTax = mF_Lump + mF_Sip;
      
      // Calculate tax at each year (proportional)
      const yearInvestment = lumpsum + (activeSip * 12 * i);
      const yearProfit = mfValueBeforeTax - yearInvestment;
      const yearTaxableGains = Math.max(0, yearProfit - 125000);
      const yearTax = yearTaxableGains * 0.125;
      const mfValueAfterTax = mfValueBeforeTax - yearTax;

      yearlyData.push({
        year: i,
        propertyValue: propVal,
        mfValueBeforeTax: mfValueBeforeTax,
        mfValueAfterTax: mfValueAfterTax,
        newFlatValue: newFlatVal
      });
    }

    return {
      downPayment: dp,
      loanAmount: loan,
      emi,
      defaultSip,
      activeSip,
      lumpsum,
      fvProperty,
      fvNewFlat,
      fvLumpsum,
      fvSip,
      totalMfValueBeforeTax,
      totalMfValueAfterTax,
      totalMFInvestment,
      mfProfit,
      mfTaxableGains,
      mfLTCGTax,
      savingsAfterNewFlat,
      wealthDifference,
      yearlyData
    };
  }, [formData]);

  // Chart: Wealth Growth Over Time (Updated with After-Tax MF values)
  const growthChartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const year = params[0].axisValue;
        return `<strong>Year ${year}</strong><br/>
                MF Portfolio (Before Tax): ${formatCurrency(params[0].value)}<br/>
                MF Portfolio (After Tax): ${formatCurrency(params[1].value)}<br/>
                Property Value (Buy Route): ${formatCurrency(params[2].value)}<br/>
                New Flat Price: ${formatCurrency(params[3].value)}`;
      }
    },
    legend: {
      data: ['MF Portfolio (Before Tax)', 'MF Portfolio (After Tax)', 'Property Value (Buy Route)', 'New Flat Price'],
      top: 25
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: calculations.yearlyData.map(item => item.year),
      name: 'Year'
    },
    yAxis: {
      type: 'value',
      name: 'Value (₹)',
      axisLabel: {
        formatter: (value) => `₹${(value / 10000000).toFixed(1)}Cr`
      }
    },
    series: [
      {
        name: 'MF Portfolio (Before Tax)',
        type: 'line',
        data: calculations.yearlyData.map(item => item.mfValueBeforeTax),
        itemStyle: {color: '#94a3b8'},
        lineStyle: {width: 2, type: 'dashed'}
      },
      {
        name: 'MF Portfolio (After Tax)',
        type: 'line',
        data: calculations.yearlyData.map(item => item.mfValueAfterTax),
        itemStyle: {color: '#10b981'},
        lineStyle: {width: 3}
      },
      {
        name: 'Property Value (Buy Route)',
        type: 'line',
        data: calculations.yearlyData.map(item => item.propertyValue),
        itemStyle: {color: '#3b82f6'},
        lineStyle: {width: 3}
      },
      {
        name: 'New Flat Price',
        type: 'line',
        data: calculations.yearlyData.map(item => item.newFlatValue),
        itemStyle: {color: '#f59e0b'},
        lineStyle: {width: 2, type: 'dashed'}
      }
    ]
  };

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{scale: 0.9, opacity: 0}}
        animate={{scale: 1, opacity: 1}}
        exit={{scale: 0.9, opacity: 0}}
        className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[95vh] overflow-y-auto my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <SafeIcon icon={FiHome} className="text-white text-sm" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Rent vs Buy – Time Value Calculator
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Input Section */}
            <div className="lg:col-span-1 space-y-6">
              {/* Scenario A: Buy Details */}
              <div className="bg-blue-50 rounded-xl shadow-sm border border-blue-200 p-6">
                <h4 className="text-md font-semibold text-blue-900 mb-4 flex items-center">
                  <SafeIcon icon={FiHome} className="mr-2" />
                  Scenario A: Buy Flat
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Flat Value Today ₹
                    </label>
                    <input
                      type="number"
                      value={formData.propertyValue}
                      onChange={(e) => handleInputChange('propertyValue', e.target.value)}
                      className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-blue-700 mt-1">{formatCurrency(formData.propertyValue)}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-2">Down Payment %</label>
                      <input
                        type="number"
                        value={formData.downPaymentPercent}
                        onChange={(e) => handleInputChange('downPaymentPercent', e.target.value)}
                        className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-2">Value ₹</label>
                      <input
                        type="text"
                        value={formatCurrency(calculations.downPayment)}
                        disabled
                        className="w-full px-4 py-3 border border-blue-200 bg-blue-100 rounded-lg text-gray-600"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-2">Registration ₹</label>
                      <input
                        type="number"
                        value={formData.registrationCost}
                        onChange={(e) => handleInputChange('registrationCost', e.target.value)}
                        className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-2">Interiors ₹</label>
                      <input
                        type="number"
                        value={formData.interiorsCost}
                        onChange={(e) => handleInputChange('interiorsCost', e.target.value)}
                        className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-2">Loan Tenure (Yrs)</label>
                      <input
                        type="number"
                        value={formData.loanTenure}
                        onChange={(e) => handleInputChange('loanTenure', e.target.value)}
                        className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-2">Interest Rate %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.interestRate}
                        onChange={(e) => handleInputChange('interestRate', e.target.value)}
                        className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-white rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-800 mb-1">Calculated EMI</p>
                    <p className="text-xl font-bold text-blue-900">{formatCurrency(calculations.emi)}/month</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-2">Property Appreciation %</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.propertyAppreciation}
                      onChange={(e) => handleInputChange('propertyAppreciation', e.target.value)}
                      className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Scenario B: Rent & Invest Details */}
              <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-6">
                <h4 className="text-md font-semibold text-green-900 mb-4 flex items-center">
                  <SafeIcon icon={FiTrendingUp} className="mr-2" />
                  Scenario B: Rent & Invest
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">
                      Monthly Rent ₹
                    </label>
                    <input
                      type="number"
                      value={formData.monthlyRent}
                      onChange={(e) => handleInputChange('monthlyRent', e.target.value)}
                      className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="p-3 bg-white rounded-lg border border-green-200">
                    <p className="text-xs text-green-800 mb-1">Initial Lumpsum Available</p>
                    <p className="text-xl font-bold text-green-900">{formatCurrency(calculations.lumpsum)}</p>
                    <p className="text-[10px] text-gray-500 mt-1">Downpayment + Reg + Interiors</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">
                      Monthly SIP (Default: EMI - Rent) ₹
                    </label>
                    <input
                      type="number"
                      value={formData.customSip || ''}
                      onChange={(e) => handleInputChange('customSip', e.target.value)}
                      placeholder={Math.round(calculations.defaultSip).toString()}
                      className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                    <p className="text-xs text-green-700 mt-1">
                      Using: {formatCurrency(calculations.activeSip)}/month
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-green-800 mb-2">MF Expected Return %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.mfReturn}
                        onChange={(e) => handleInputChange('mfReturn', e.target.value)}
                        className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-green-800 mb-2">New Flat Appr. %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.newFlatAppreciation}
                        onChange={(e) => handleInputChange('newFlatAppreciation', e.target.value)}
                        className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* Key Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Buy Route Summary */}
                <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Scenario A: Buy Flat Today</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Initial Outflow:</span>
                      <span className="text-md font-bold text-gray-900">{formatCurrency(calculations.lumpsum)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Monthly EMI:</span>
                      <span className="text-md font-bold text-gray-900">{formatCurrency(calculations.emi)}</span>
                    </div>
                    <div className="pt-4 border-t border-gray-100">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Final Asset Value (Year {formData.loanTenure})</span>
                      <p className="text-3xl font-black text-blue-600 mt-1">{formatCurrency(calculations.fvProperty)}</p>
                    </div>
                  </div>
                </div>

                {/* Rent Route Summary */}
                <div className="bg-white rounded-xl shadow-sm border border-green-200 p-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-green-500"></div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Scenario B: Rent & Invest</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Initial MF Lumpsum:</span>
                      <span className="text-md font-bold text-gray-900">{formatCurrency(calculations.lumpsum)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Monthly SIP:</span>
                      <span className="text-md font-bold text-gray-900">{formatCurrency(calculations.activeSip)}</span>
                    </div>
                    <div className="pt-4 border-t border-gray-100">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Final MF Corpus (After Tax)</span>
                      <p className="text-3xl font-black text-green-600 mt-1">{formatCurrency(calculations.totalMfValueAfterTax)}</p>
                      <p className="text-xs text-gray-500 mt-1">Before Tax: {formatCurrency(calculations.totalMfValueBeforeTax)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* LTCG Tax Breakdown */}
              <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-6">
                <h4 className="text-md font-semibold text-orange-900 mb-4">LTCG Tax Breakdown (Scenario B: MF Route)</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs text-orange-700 font-medium mb-1">Total Investment</p>
                    <p className="text-lg font-bold text-orange-900">{formatCurrency(calculations.totalMFInvestment)}</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs text-orange-700 font-medium mb-1">MF Value (Before Tax)</p>
                    <p className="text-lg font-bold text-orange-900">{formatCurrency(calculations.totalMfValueBeforeTax)}</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs text-orange-700 font-medium mb-1">Profit</p>
                    <p className="text-lg font-bold text-green-700">{formatCurrency(calculations.mfProfit)}</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs text-orange-700 font-medium mb-1">Exemption</p>
                    <p className="text-lg font-bold text-blue-700">₹1,25,000</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs text-orange-700 font-medium mb-1">Taxable Gains</p>
                    <p className="text-lg font-bold text-orange-900">{formatCurrency(calculations.mfTaxableGains)}</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-xs text-red-700 font-medium mb-1">LTCG Tax (12.5%)</p>
                    <p className="text-lg font-bold text-red-700">{formatCurrency(calculations.mfLTCGTax)}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg border-2 border-green-400 col-span-2">
                    <p className="text-xs text-green-700 font-medium mb-1">MF Value (After Tax)</p>
                    <p className="text-2xl font-black text-green-900">{formatCurrency(calculations.totalMfValueAfterTax)}</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-xs text-yellow-800">
                    <strong>LTCG Tax:</strong> 12.5% tax on profits above ₹1,25,000 exemption (for MF investments held &gt;1 year)
                  </p>
                </div>
              </div>

              {/* The "New Flat" Decision Box */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-lg p-8 text-white relative overflow-hidden">
                <SafeIcon icon={FiPieChart} className="absolute -right-4 -bottom-4 text-gray-700 opacity-30 text-9xl" />
                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-6 flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="text-green-400 mr-3 text-2xl" />
                    Ultimate Wealth Comparison (After {formData.loanTenure} Years)
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Net Wealth (Buy)</p>
                      <p className="text-2xl font-bold">{formatCurrency(calculations.fvProperty)}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Net Wealth (Rent - After Tax)</p>
                      <p className="text-2xl font-bold">{formatCurrency(calculations.totalMfValueAfterTax)}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm border border-white/20">
                      <p className="text-gray-300 text-xs uppercase tracking-wider mb-1">Difference</p>
                      <p className={`text-2xl font-bold ${
                        calculations.wealthDifference > 0 ? 'text-green-400' : 'text-blue-400'
                      }`}>
                        {formatCurrency(Math.abs(calculations.wealthDifference))}
                      </p>
                      <p className="text-[10px] mt-1">
                        {calculations.wealthDifference > 0 ? 'in favor of Renting' : 'in favor of Buying'}
                      </p>
                    </div>
                  </div>

                  {/* New Flat Purchase Capability */}
                  <div className="bg-orange-500/20 border border-orange-500/30 rounded-lg p-5">
                    <h4 className="text-orange-300 font-semibold mb-2 flex items-center">
                      <SafeIcon icon={FiInfo} className="mr-2" />
                      The "Upgrade to a New Flat" Scenario
                    </h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      If you choose to <strong>Rent & Invest</strong>, after {formData.loanTenure} years, a brand new flat (growing at {formData.newFlatAppreciation}% p.a.) will cost <strong className="text-white">{formatCurrency(calculations.fvNewFlat)}</strong>.
                      <br/><br/>
                      Your MF Corpus (after LTCG tax) will be <strong className="text-white">{formatCurrency(calculations.totalMfValueAfterTax)}</strong>.
                    </p>
                    <div className="mt-4 pt-4 border-t border-orange-500/30">
                      {calculations.savingsAfterNewFlat >= 0 ? (
                        <p className="text-lg text-green-400 font-bold">
                          You can buy the new flat outright AND still have {formatCurrency(calculations.savingsAfterNewFlat)} left in savings!
                        </p>
                      ) : (
                        <p className="text-lg text-red-400 font-bold">
                          You will be short by {formatCurrency(Math.abs(calculations.savingsAfterNewFlat))} to buy the new flat outright.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Wealth Growth Trajectory</h4>
                <div className="h-96">
                  <ReactECharts option={growthChartOption} style={{height: '100%', width: '100%'}} />
                </div>
              </div>

              {/* Summary Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Detailed Breakdown at Year {formData.loanTenure}</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Component</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Buy Route</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Rent Route</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">Initial Investment</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(calculations.lumpsum)}</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(calculations.lumpsum)} (in MF)</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">Monthly Outflow</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(calculations.emi)} (EMI)</td>
                        <td className="py-3 px-4 text-right text-gray-900">
                          {formatCurrency(formData.monthlyRent)} (Rent) + {formatCurrency(calculations.activeSip)} (SIP)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">Total Investment</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(calculations.lumpsum)}</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(calculations.totalMFInvestment)}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">Asset Value (Before Tax)</td>
                        <td className="py-3 px-4 text-right text-blue-700 font-semibold">{formatCurrency(calculations.fvProperty)}</td>
                        <td className="py-3 px-4 text-right text-gray-700 font-semibold">{formatCurrency(calculations.totalMfValueBeforeTax)}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">LTCG Tax</td>
                        <td className="py-3 px-4 text-right text-gray-900">-</td>
                        <td className="py-3 px-4 text-right text-red-600 font-semibold">-{formatCurrency(calculations.mfLTCGTax)}</td>
                      </tr>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-bold">Net Wealth (After Tax)</td>
                        <td className="py-3 px-4 text-right text-blue-700 font-bold">{formatCurrency(calculations.fvProperty)}</td>
                        <td className="py-3 px-4 text-right text-green-700 font-bold">{formatCurrency(calculations.totalMfValueAfterTax)}</td>
                      </tr>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-bold">Net Wealth Difference</td>
                        <td className="py-3 px-4 text-right text-gray-900 font-bold" colSpan="2">
                          <span className={calculations.wealthDifference > 0 ? 'text-green-600' : 'text-blue-600'}>
                            {formatCurrency(Math.abs(calculations.wealthDifference))} {calculations.wealthDifference > 0 ? 'higher in Rent Route' : 'higher in Buy Route'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default RentVsBuyCalculator;